
import {finalize} from 'rxjs/operators';
import { map, find } from 'lodash';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';

import { Operation } from '../operation/operation';
import { Revision } from '../revision/revision';
import { Document } from './document';
import { DocumentService } from './document.service';
import { NotifierService, DialogService } from '../ui';
import { DossierModel, Target } from '../dossier-model/dossier-model';

import { FileUploader, FileItem, ParsedResponseHeaders } from 'ng2-file-upload';

type ViewMode = 'new' | 'edit' | 'view';

@Component({
  templateUrl: './document-edit.component.html',
})
export class DocumentEditComponent implements OnInit {

  operation: Operation
  revision: Revision
  document: Document
  dossierModel: DossierModel

  isFileOver: boolean = false
  uploader: FileUploader
  
  documentErrors: any
  
  ERROR_TYPE = {
    EMPTY_TAG: 'Balise vide',
    SYNTAX: 'Erreur de syntaxe',
    NO_BLOCK_END: 'Bloc non terminé',
    UNEXPECTED_END: 'Fin de bloc inattendue',
    SAME_LEVEL: 'Début et fin du bloc à des niveaux différents'
  };

  SECTION_NAME = {
    BODY: 'Corps du document',
    HEADER: 'En-tête de page',
    FOOTER: 'Pied de page'
  };

  mode: ViewMode
  title: string

  name: FormControl
  target: FormControl

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private documentService: DocumentService,
    private notifier: NotifierService,
    private builder: FormBuilder,
    private dialogService: DialogService
  ) { }

  initForm() {
    // name
    this.name = new FormControl(this.document.name, [
      Validators.required
    ]);

    this.name.valueChanges.subscribe((value) => {
      this.document.name = value;
    });

    // temporary_file_id
    this.uploader = new FileUploader({
      url: '/files/documents',
      autoUpload: true
    });

    this.uploader.onSuccessItem = (item: FileItem, response: string, status: number, headers: ParsedResponseHeaders) => {
      let result = JSON.parse(response);

      if (result.success) {
        this.document.temporary_file_id = result.file.id;
        this.document.filename = result.file.filename;

        this.documentErrors = null;

        // Auto save the document
        if (this.mode === 'edit' && this.document.valid) {
          this.save();
        }
        else {
          this.notifier.success('Document .docx valide');
        }
      }
      else {
        if (result.message) {
          this.notifier.error(result.message);

          this.document.temporary_file_id = null;
          this.document.filename = null;

          this.documentErrors = result.errors;
        }
      }
    };

    this.uploader.onBeforeUploadItem = (fileItem: FileItem) => {
      this.notifier.updateProgress(0);

      return {fileItem};
    }

    this.uploader.onProgressItem = (fileItem: FileItem, progress: any) => {
      this.notifier.updateProgress(progress);

      return {
        fileItem,
        progress
      }
    }

    this.uploader.onCompleteAll = () => {
      this.notifier.endProgress();
    };
  }

  ngOnInit() {

    this.mode = this.route.routeConfig.data.mode;
    this.title = this.route.routeConfig.data.title;

    // Get the operation/revision (document optional)
    this.notifier.startProgress();

    this.route.data.subscribe((data) => {
      this.operation = data.operation;
      this.revision = data.operation.revision;
      this.dossierModel = data.dossierModel;

      this.notifier.endProgress();
    });

    switch (this.mode) {
      case 'new':
        {
          this.document = new Document();
          this.document.name = '';

          this.initForm();

          this.document.target = find<Target>(this.dossierModel.targets, t => t.link.is_root).id;
        }
        break;

      case 'edit':
        {
          this.document = this.revision.document;

          this.initForm();
        }
        break;

      case 'view':
        {
          this.document = this.revision.document;
        }
        break;
    }
  }

  save() {
    if (!this.document || !this.document.valid) {
      return;
    }

    let notifier = this.notifier;

    notifier.updateProgress(20);

    function onComplete() {
      notifier.endProgress();
    }

    switch (this.mode) {
      case 'new':
        {
          this.documentService
            .create(this.operation.id, this.revision.id, this.document).pipe(
            finalize(onComplete))
            .subscribe((document) => {
              this.document = document;

              this.notifier.success('Document créé avec succès');

              // Got to edit screen
              this.router.navigate(['operations', this.operation.id, this.revision.id, document.id, 'edit']);
            });
        }
        break;

      case 'edit':
        {
          this.documentService
            .update(this.operation.id, this.revision.id, this.document.id, this.document).pipe(
            finalize(onComplete))
            .subscribe((document) => {
              this.document = document;

              this.notifier.success('Document enregistré avec succès');
            });
        }
        break;
    }
  }

  cancel() {
    if (this.mode === 'edit') {
      this.router.navigate(['operations', this.operation.id, this.revision.id, this.document.id]);
    }
    else {
      this.router.navigate(['operations', this.operation.id, this.revision.id]);
    }
  }

  delete() {
    this.dialogService
      .confirmDelete('Voulez-vous supprimer ce document?')
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.notifier.startProgress();

        this.documentService
          .delete(this.operation.id, this.revision.id, this.document.id)
          .subscribe((id) => {
            this.notifier.success('Document supprimé avec succès');

            this.router.navigate(['operations', this.operation.id, this.revision.id]);

          }, (err) => {
            this.notifier.endProgress();
          });
      });
    
    return false;
  }
}
